import { useEffect, useRef, useState } from 'react';
import { submitContribution, getSessionId, type ContactInfo, type NamingContext } from '../api/participation-api';
import type { MapSelection } from './MapPicker';
import { CONTRIBUTION_CATEGORIES, GuidedQuestions, buildGuidedComment } from './GuidedQuestions';
import '../participation.css';

type ModalScale = 'city' | 'sector' | 'place';
type ModalVariant = 'modal' | 'panel';
type Mode = 'free' | 'guided';

// Provisional reference areas for sector scale — provisional until real sector
// boundaries are defined and loaded in the DB as named territorial units.
const REFERENCE_AREAS = [
  'Centro histórico y zona patrimonial',
  'Barrios consolidados',
  'Periferias y crecimiento urbano',
  'Humedales y áreas ambientales',
  'Localidades rurales y comunidades',
  'Corredores de acceso y movilidad',
] as const;

const SCALE_LABEL: Record<ModalScale, string> = {
  city:   'Ciudad completa',
  sector: 'Sector urbano',
  place:  'Calle o lugar',
};

interface ContributionModalProps {
  scale: ModalScale;
  slug: string;
  selection?: MapSelection;
  onClose: () => void;
  onSuccess: () => void;
  variant?: ModalVariant;
}

// ── Success screen ────────────────────────────────────────────────────────────

function SuccessContent({ onClose, onAnother }: { onClose: () => void; onAnother: () => void }): JSX.Element {
  return (
    <div style={{ textAlign: 'center', padding: '16px 0 8px' }}>
      <div className="success-check">✓</div>
      <h3 style={{ margin: '0 0 10px', fontSize: 20, fontWeight: 700, color: '#1a1a2e' }}>
        ¡Gracias por tu aporte!
      </h3>
      <p style={{ margin: '0 0 28px', fontSize: 14, color: '#6b7280', lineHeight: 1.65 }}>
        Tu participación contribuye al diagnóstico urbano de San Cristóbal de las Casas.
      </p>
      <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
        <button
          type="button"
          onClick={onAnother}
          style={{ padding: '9px 20px', borderRadius: 10, border: '1.5px solid #e0e0e0', background: '#fff', color: '#374151', fontSize: 13.5, fontWeight: 600, cursor: 'pointer' }}
        >
          Agregar otro
        </button>
        <button
          type="button"
          onClick={onClose}
          style={{ padding: '9px 20px', borderRadius: 10, border: 'none', background: '#1a1a2e', color: '#fff', fontSize: 13.5, fontWeight: 600, cursor: 'pointer' }}
        >
          Cerrar
        </button>
      </div>
    </div>
  );
}

// ── Zone picker — first step for sector scale ─────────────────────────────────

function ZonePicker({ onConfirm }: { onConfirm: (zone: string) => void }): JSX.Element {
  const [zone, setZone] = useState('');
  return (
    <div>
      <p style={{ margin: '0 0 8px', fontSize: 12.5, fontWeight: 600, color: '#6b7280', letterSpacing: '0.04em', textTransform: 'uppercase' }}>
        Elige un ámbito de referencia
      </p>
      <select
        className="category-select"
        value={zone}
        onChange={e => setZone(e.target.value)}
        style={{ marginBottom: 18 }}
      >
        <option value="" disabled>Elige un ámbito…</option>
        {REFERENCE_AREAS.map(z => (
          <option key={z} value={z}>{z}</option>
        ))}
      </select>
      <button
        type="button"
        className="submit-btn"
        disabled={!zone}
        onClick={() => onConfirm(zone)}
      >
        Continuar →
      </button>
      <p className="zone-picker-note">
        Ámbitos de referencia provisionales — las zonas definitivas se definirán con base en el diagnóstico urbano.
      </p>
    </div>
  );
}

// ── Contact section (optional, at end of form) ────────────────────────────────

interface ContactSectionState {
  agreed: boolean;
  name: string;
  email: string;
  phone: string;
  emailError: boolean;
}

function ContactSection({
  state,
  onChange,
}: {
  state: ContactSectionState;
  onChange: (s: ContactSectionState) => void;
}): JSX.Element {
  function update(patch: Partial<ContactSectionState>): void {
    onChange({ ...state, ...patch });
  }

  function validateEmail(v: string): boolean {
    if (!v) return true; // optional
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
  }

  return (
    <div className="contact-section">
      <p className="contact-section-title">¿Quieres que el equipo pueda contactarte?</p>

      <label className="contact-toggle-row">
        <input
          type="checkbox"
          checked={state.agreed}
          onChange={e => update({ agreed: e.target.checked, emailError: false })}
        />
        <span className="contact-toggle-label">
          Sí, acepto dejar mis datos de contacto
        </span>
      </label>

      {state.agreed && (
        <div className="contact-fields">
          <input
            type="text"
            className="contact-input"
            placeholder="Nombre (opcional)"
            value={state.name}
            maxLength={120}
            onChange={e => update({ name: e.target.value })}
          />
          <input
            type="email"
            className={`contact-input${state.emailError ? ' error' : ''}`}
            placeholder="Correo electrónico (opcional)"
            value={state.email}
            maxLength={200}
            onChange={e => update({ email: e.target.value, emailError: !validateEmail(e.target.value) })}
          />
          {state.emailError && (
            <span style={{ fontSize: 11.5, color: '#dc2626', marginTop: -6 }}>
              Formato de correo inválido
            </span>
          )}
          <input
            type="tel"
            className="contact-input"
            placeholder="Teléfono (opcional, ej. +52 967 000 0000)"
            value={state.phone}
            maxLength={40}
            onChange={e => update({ phone: e.target.value })}
          />
        </div>
      )}

      <p className="contact-privacy-note">
        Tus datos solo se usarán si el equipo necesita ampliar información sobre tu aporte.
      </p>
    </div>
  );
}

// ── Main form ─────────────────────────────────────────────────────────────────

function ContributionForm({
  scale,
  slug,
  zone,
  selection,
  onSuccess,
  onClose,
}: {
  scale: ModalScale;
  slug: string;
  zone?: string;
  selection?: MapSelection;
  onSuccess: () => void;
  onClose: () => void;
}): JSX.Element {
  const [mode, setMode]       = useState<Mode>('free');
  const [category, setCategory] = useState('');
  const [answers, setAnswers] = useState<string[]>(['', '', '']);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError]     = useState('');
  const [done, setDone]       = useState(false);

  const [contact, setContact] = useState<ContactSectionState>({
    agreed: false,
    name: '',
    email: '',
    phone: '',
    emailError: false,
  });

  function resetForm(): void {
    setMode('free');
    setCategory('');
    setAnswers(['', '', '']);
    setComment('');
    setError('');
    setDone(false);
    setContact({ agreed: false, name: '', email: '', phone: '', emailError: false });
  }

  async function handleSubmit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    if (!category) return;
    // Validate email before submit
    if (contact.agreed && contact.email && contact.emailError) return;

    setSubmitting(true);
    setError('');
    try {
      const finalComment = mode === 'guided'
        ? buildGuidedComment(category, answers, comment)
        : (comment.trim() || undefined);

      const contactInfo: ContactInfo | undefined = {
        contactRequested: contact.agreed,
        ...(contact.agreed && {
          contactName:  contact.name  || undefined,
          contactEmail: contact.email || undefined,
          contactPhone: contact.phone || undefined,
        }),
      };

      const namingContext: NamingContext | undefined = selection?.streetLabel
        ? {
            streetLabel: selection.streetLabel.displayLabel,
            streetName:  selection.streetLabel.primaryName ?? null,
            fromStreet:  selection.streetLabel.fromStreet  ?? null,
            toStreet:    selection.streetLabel.toStreet    ?? null,
            confidence:  selection.streetLabel.confidence,
            source:      selection.streetLabel.source,
            warnings:    selection.streetLabel.warnings,
          }
        : undefined;

      await submitContribution(slug, {
        scale,
        category,
        comment: finalComment || undefined,
        geometry: selection?.geometry,
        canonicalEdgeId: selection?.canonicalEdgeId,
        h3CellId: selection?.h3CellId,
        sessionId: getSessionId(),
        contactInfo,
        zoneName: zone || undefined,
        namingContext,
      });
      setDone(true);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Error al enviar. Intenta de nuevo.');
    } finally {
      setSubmitting(false);
    }
  }

  if (done) {
    return <SuccessContent onClose={onClose} onAnother={() => { resetForm(); onSuccess(); }} />;
  }

  const guidedComplete = mode !== 'guided' || answers.some(a => a !== '');
  const canSubmit = !!category && guidedComplete && !submitting && !contact.emailError;

  return (
    <form onSubmit={handleSubmit}>
      {/* Zone tag (sector only) */}
      {zone && (
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'rgba(59,130,246,0.08)', border: '1px solid rgba(59,130,246,0.2)', borderRadius: 20, padding: '3px 12px', marginBottom: 20, fontSize: 12.5, color: '#3b82f6', fontWeight: 600 }}>
          {zone}
        </div>
      )}

      {/* Mode toggle */}
      <div style={{ marginBottom: 20 }}>
        <p style={{ margin: '0 0 10px', fontSize: 12.5, fontWeight: 600, color: '#6b7280', letterSpacing: '0.04em', textTransform: 'uppercase' }}>
          ¿Cómo quieres aportar?
        </p>
        <div className="mode-toggle">
          <button type="button" className={mode === 'free' ? 'active' : ''} onClick={() => setMode('free')}>
            Comentario libre
          </button>
          <button type="button" className={mode === 'guided' ? 'active' : ''} onClick={() => setMode('guided')}>
            Preguntas guiadas
          </button>
        </div>
      </div>

      {/* Category */}
      <div style={{ marginBottom: 22 }}>
        <label style={{ display: 'block', fontSize: 12.5, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 8 }}>
          Categoría / Tema
        </label>
        <select
          className="category-select"
          value={category}
          onChange={e => { setCategory(e.target.value); setAnswers(['', '', '']); }}
          required
        >
          <option value="" disabled>Selecciona un tema…</option>
          {CONTRIBUTION_CATEGORIES.map(c => (
            <option key={c} value={c}>{c}</option>
          ))}
        </select>
      </div>

      {/* Free comment OR Guided questions */}
      {mode === 'free' ? (
        <div style={{ marginBottom: 22 }}>
          <label style={{ display: 'block', fontSize: 12.5, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 8 }}>
            Comentario
          </label>
          <textarea
            className="participation-textarea"
            value={comment}
            onChange={e => setComment(e.target.value)}
            maxLength={2000}
            rows={4}
            placeholder="Describe tu aporte, propuesta o problema…"
          />
          <div style={{ textAlign: 'right', fontSize: 11.5, color: '#c4c9d4', marginTop: 4 }}>
            {comment.length}/2000
          </div>
        </div>
      ) : (
        category && (
          <div style={{ marginBottom: 22 }}>
            <GuidedQuestions category={category} answers={answers} onChange={setAnswers} />
            <label style={{ display: 'block', fontSize: 12.5, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 8, marginTop: 4 }}>
              Comentario adicional (opcional)
            </label>
            <textarea
              className="participation-textarea no-resize"
              value={comment}
              onChange={e => setComment(e.target.value)}
              maxLength={1000}
              rows={2}
              placeholder="Algo más que quieras agregar…"
            />
          </div>
        )
      )}

      {/* Optional contact section */}
      <ContactSection state={contact} onChange={setContact} />

      {error && (
        <p style={{ margin: '14px 0', fontSize: 13, color: '#dc2626', background: '#fef2f2', padding: '10px 14px', borderRadius: 8, lineHeight: 1.5 }}>
          {error}
        </p>
      )}

      <button type="submit" className="submit-btn" disabled={!canSubmit} style={{ marginTop: 18 }}>
        {submitting ? 'Enviando…' : 'Enviar aporte'}
      </button>
    </form>
  );
}

// ── Main export ───────────────────────────────────────────────────────────────

export function ContributionModal({
  scale,
  slug,
  selection,
  onClose,
  onSuccess,
  variant = 'modal',
}: ContributionModalProps): JSX.Element {
  const overlayRef = useRef<HTMLDivElement>(null);
  const [zone, setZone] = useState('');

  const needsZone = scale === 'sector' && !zone;

  function handleBackdrop(e: React.MouseEvent): void {
    if (e.target === overlayRef.current) onClose();
  }

  useEffect(() => {
    function onKey(e: KeyboardEvent): void { if (e.key === 'Escape') onClose(); }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const panelContent = (
    <>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 20 }}>
        <div>
          <p style={{ margin: '0 0 2px', fontSize: 11.5, fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
            Aporte sobre este tramo
          </p>
          <h2 style={{ margin: '0 0 2px', fontSize: 18, fontWeight: 700, color: '#1a1a2e', lineHeight: 1.25 }}>
            {selection?.label ?? SCALE_LABEL[scale]}
          </h2>
          {selection?.sublabel && (
            <p style={{ margin: 0, fontSize: 12, color: '#94a3b8', lineHeight: 1.4 }}>
              {selection.sublabel}
            </p>
          )}
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Cerrar"
          style={{ width: 32, height: 32, borderRadius: '50%', border: '1.5px solid #e8e8e8', background: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, color: '#9ca3af', flexShrink: 0, marginLeft: 12 }}
        >
          ✕
        </button>
      </div>

      {needsZone ? (
        <ZonePicker onConfirm={setZone} />
      ) : (
        <ContributionForm
          scale={scale}
          slug={slug}
          zone={zone || undefined}
          selection={selection}
          onSuccess={onSuccess}
          onClose={onClose}
        />
      )}
    </>
  );

  if (variant === 'panel') {
    return <div className="place-panel">{panelContent}</div>;
  }

  return (
    <div ref={overlayRef} className="participation-modal-overlay" onClick={handleBackdrop}>
      <div className="participation-modal">{panelContent}</div>
    </div>
  );
}
